/* global AudioContext */
export interface IAnalyserOptions {
  fftSize: number;
  smoothingTimeConstant: number;
  minDecibels: number;
  maxDecibels: number;
}

/**
 * A Readonly Wrapper for an AnalyserNode.
 *
 * Provides a minimal interface into the analyser's API.
 *
 * NOTE: Readonly<AnalyserNode> does not provide the correct behavior
 * (as of TS 3.4) because type scripts allows passing readonly
 * parameters into functions with mutable views of the same data (essentially
 * it erases readonly types in function calls).
 */
export class ReadonlyAnalyserNode {
  public constructor(private readonly analyser: Pick<AnalyserNode,
    'fftSize' |
    'smoothingTimeConstant' |
    'minDecibels' |
    'maxDecibels' |
    'frequencyBinCount' |
    'getByteFrequencyData'
    > & Partial<Readonly<AnalyserNode>>,
  ) {}

  public get fftSize(): number {
    return this.analyser.fftSize;
  }

  public get smoothingTimeConstant(): number {
    return this.analyser.smoothingTimeConstant;
  }

  public get minDecibels(): number {
    return this.analyser.minDecibels;
  }

  public get maxDecibels(): number {
    return this.analyser.maxDecibels;
  }

  public get frequencyBinCount(): number {
    return this.analyser.frequencyBinCount;
  }

  public get context(): BaseAudioContext | undefined {
    return this.analyser.context;
  }

  public getByteFrequencyData = (buffer: Uint8Array) => this.analyser.getByteFrequencyData(buffer);
}

export interface IWebAudio {
  /**
   * Get an instance of the AudioContext.
   *
   * If the service does not already have hold of an AudioContext, or if the one it has
   * is in the closed state, it will return a new one.
   *
   * In order to get the audio analysis tools working with Safari, we have to persist a
   * single instance of the resources after they are initial created. Safari limits the
   * number of times you can create new AudioContexts in a given time window.
   *
   * We can't keep around just a constant because audio contexts
   * can only be created as the result of a user action.
   */
  getAudioContext: () => AudioContext | null;
  /**
   * Close and release ownership of the underlying context resources.
   */
  closeAudioContext: () => Promise<void>;
  /**
   * Factory method for making an analysers for a given stream
   *
   * @param stream - the stream to analyse
   * @param options - configuration settings for the analyser
   * @returns anaylser - an audio analyser for the stream
   */
  makeAnalyser: (stream: MediaStream, options?: Partial<IAnalyserOptions>) => ReadonlyAnalyserNode;

  /**
   * Make a dummy analyser.
   * Helpful for error cases.
   * @returns dummy - something that looks like an analyser node, but returns all zeros;
   */
  makeDummyAnalyser: () => ReadonlyAnalyserNode;

  makeMediaStreamSource: (stream: MediaStream) => MediaStreamAudioSourceNode;

  makeMediaStreamDestination: () => MediaStreamAudioDestinationNode;
}

export default class WebAudio implements IWebAudio {
  // Null here becuase Safari returns null when it can't create
  // any more audio contexts
  private ctx: AudioContext | null = null;

  public getAudioContext = () => {
    if (this.ctx === null || this.ctx.state === 'closed') {
      this.ctx = new AudioContext();
    }
    return this.ctx;
  };

  public closeAudioContext = async () => {
    if (this.ctx) {
      await this.ctx.close();
    }
    this.ctx = null;
  };

  public makeAnalyser = (stream: MediaStream, options?: Partial<IAnalyserOptions>) => {
    const audio = this.getAudioContext();
    if (!audio) {
      return this.makeDummyAnalyser();
    }
    const source = audio.createMediaStreamSource(stream);
    const analyser: AnalyserNode = Object.assign(audio.createAnalyser(), options);
    source.connect(analyser);
    return new ReadonlyAnalyserNode(analyser);
  };

  public makeDummyAnalyser = () => {
    return new ReadonlyAnalyserNode({
      fftSize: 0,
      smoothingTimeConstant: 0,
      minDecibels: 0,
      maxDecibels: 0,
      frequencyBinCount: 0,
      getByteFrequencyData: (buff: Uint8Array) => buff.fill(0),
    });
  };

  public makeMediaStreamSource = (stream: MediaStream) => {
    return this.requireAudioContext().createMediaStreamSource(stream);
  };

  public makeMediaStreamDestination = () => {
    return this.requireAudioContext().createMediaStreamDestination();
  };

  /**
   * Private helper for methods that won't work without instance
   * of AudioContext
   */
  private requireAudioContext(): AudioContext {
    const audio = this.getAudioContext();

    if (!audio) {
      throw new Error('No AudioContext');
    }

    return audio;
  }
}
